import * as _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useAuthenticatedUser, useSearch } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Card,
	Empty,
	FlexGap,
	Heading3,
	Input,
	ListRow,
	Modal,
	Paragraph,
	SearchIcon,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import { JobTitleNameSource, useJobTitleName } from '../job/useJobTitleName';

function ComplimentRecipient(props: {
	user: {
		id: string;
		firstName: string;
		lastName: string;
		jobPositions?: { jobTitle: JobTitleNameSource }[];
	};
}) {
	const jobTitleName = useJobTitleName();

	const jobTitle = props.user.jobPositions?.[0]?.jobTitle;

	return (
		<ListRow to={`/users/${props.user.id}/feedback/compliments/new`}>
			<FlexGap alignItems="center" gap={tokens.gap.element} minWidth={0}>
				<Avatar user={props.user} size={32} />
				<Box flexGrow={1} flexShrink={1} minWidth={0}>
					<Heading3>
						{props.user.firstName} {props.user.lastName}
					</Heading3>
					{jobTitle && (
						<Paragraph mt={`${tokens.gap.inline}px`} lineClamp={1}>
							{jobTitleName(jobTitle)}
						</Paragraph>
					)}
				</Box>
			</FlexGap>
		</ListRow>
	);
}

function ComplimentRecipientSkeleton() {
	return (
		<ListRow.Skeleton>
			<FlexGap alignItems="center" gap={tokens.gap.element}>
				<Avatar.Skeleton size={32} />
				<Box flexGrow={1} flexShrink={1} minWidth={0}>
					<Heading3.Skeleton />
					<Paragraph.Skeleton mt={`${tokens.gap.inline}px`} lines={1} />
				</Box>
			</FlexGap>
		</ListRow.Skeleton>
	);
}

export default function ComplimentRecipientSelector(props: { onDismiss: () => void }) {
	const { t } = useTranslation();

	const viewer = useAuthenticatedUser();

	const [query, setQuery] = useState('');

	const targets = graphql.useComplimentTargetsQuery();

	const users = _.sortBy(
		_.uniqBy(
			targets.data?.viewer.roles.flatMap((role) => {
				if (role.targetV2?.__typename === 'Group') {
					return role.targetV2.members.edges.map(({ node }) => node);
				} else {
					return [];
				}
			}),
			(user) => user.id,
		)
			.filter(({ isArchived }) => !isArchived)
			.filter(({ id }) => id !== viewer.id),
		(user) => `${user.firstName} ${user.lastName}`,
	);

	const { loading, results } = useSearch(query, {
		skip: !query,
		filters: 'isArchived:false',
	});

	const searchResults = (
		<>
			{loading ? (
				<Card p={0}>
					<Skeleton.Repeat count={3}>
						<ComplimentRecipientSkeleton />
					</Skeleton.Repeat>
				</Card>
			) : query && results.length > 0 ? (
				<Card p={0}>
					{results.slice(0, 10).map((user) => (
						<ComplimentRecipient key={user.id} user={user} />
					))}
				</Card>
			) : (
				<Empty.Card
					small
					icon={<SearchIcon />}
					title={
						query
							? t('compliments.create.recipients.search.empty')
							: t('compliments.create.recipients.search.instruction')
					}
				/>
			)}
		</>
	);

	return (
		<Modal width={560} height="min(640px, calc(100dvh - 40px))">
			<Modal.Header
				title={t('compliments.create.recipients.search.title')}
				onClose={props.onDismiss}
			/>
			<Modal.Contents>
				<FlexGap flexDirection="column" gap={tokens.gap.section}>
					<FlexGap flexDirection="column" gap={tokens.gap.group}>
						<Input
							search
							onChange={setQuery}
							value={query}
							autoFocus
							icon={<SearchIcon />}
							placeholder={t('compliments.create.recipients.search.placeholder')}
							clearable={!!query}
						/>
						{searchResults}
					</FlexGap>
					<FlexGap flexDirection="column" gap={tokens.gap.group}>
						<Heading3>{t('compliments.create.recipients.search.department')}</Heading3>
						<Card p={0}>
							{targets.data ? (
								users.map((user) => (
									<ComplimentRecipient key={user.id} user={user as graphql.User} />
								))
							) : (
								<Skeleton.Repeat count={5}>
									<ComplimentRecipientSkeleton />
								</Skeleton.Repeat>
							)}
						</Card>
					</FlexGap>
				</FlexGap>
			</Modal.Contents>
		</Modal>
	);
}
